import { useState } from 'react';
import { Download, FileAudio, Settings } from 'lucide-react';
import { useStudioStore } from '../../store/useStudioStore';
import JSZip from 'jszip';

type ExportFormat = 'wav' | 'mp3';
type BitDepth = '16' | '24' | '32';
type SampleRate = '44100' | '48000' | '96000';

interface ExportSettings {
  format: ExportFormat;
  bitDepth: BitDepth;
  sampleRate: SampleRate;
}

export const StemExporter = () => {
  const channels = useStudioStore((state) => state.channels);
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  const [exportSettings, setExportSettings] = useState<ExportSettings>({
    format: 'wav',
    bitDepth: '24',
    sampleRate: '48000',
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Toggle track selection
  const toggleTrack = (trackId: string) => {
    setSelectedTracks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(trackId)) {
        newSet.delete(trackId);
      } else {
        newSet.add(trackId);
      }
      return newSet;
    });
  };

  // Select all tracks
  const selectAll = () => {
    setSelectedTracks(new Set(channels.map((ch) => ch.id)));
  };

  // Deselect all tracks
  const deselectAll = () => {
    setSelectedTracks(new Set());
  };

  // Convert AudioBuffer to WAV format
  const audioBufferToWav = (buffer: AudioBuffer, bitDepth: number): ArrayBuffer => {
    const numberOfChannels = buffer.numberOfChannels;
    const bytesPerSample = bitDepth / 8;
    const length = buffer.length * numberOfChannels * bytesPerSample;
    const arrayBuffer = new ArrayBuffer(44 + length);
    const view = new DataView(arrayBuffer);

    // Write WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * numberOfChannels * bytesPerSample, true);
    view.setUint16(32, numberOfChannels * bytesPerSample, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, length, true);

    // Write audio data
    const channels: Float32Array[] = [];
    for (let i = 0; i < numberOfChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    let offset = 44;
    const maxValue = Math.pow(2, bitDepth - 1) - 1;
    const minValue = -Math.pow(2, bitDepth - 1);

    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        const intSample = Math.round(sample * (sample < 0 ? -minValue : maxValue));

        if (bitDepth === 16) {
          view.setInt16(offset, intSample, true);
          offset += 2;
        } else if (bitDepth === 24) {
          // 24-bit audio (3 bytes per sample)
          view.setUint8(offset, intSample & 0xff);
          view.setUint8(offset + 1, (intSample >> 8) & 0xff);
          view.setUint8(offset + 2, (intSample >> 16) & 0xff);
          offset += 3;
        } else if (bitDepth === 32) {
          view.setInt32(offset, intSample, true);
          offset += 4;
        }
      }
    }

    return arrayBuffer;
  };

  // Render a single track to audio buffer
  const renderTrack = async (trackId: string): Promise<AudioBuffer> => {
    const track = channels.find((ch) => ch.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    // Create offline audio context for rendering
    const sampleRate = parseInt(exportSettings.sampleRate);
    const duration = 10; // 10 seconds default (in production, calculate from project length)
    const offlineContext = new OfflineAudioContext(2, sampleRate * duration, sampleRate);

    // In production, this would render the actual track with all effects
    // For now, we create a simple tone as a placeholder
    const oscillator = offlineContext.createOscillator();
    const gainNode = offlineContext.createGain();

    oscillator.frequency.value = 440; // A4 note
    gainNode.gain.value = track.volume * (track.muted ? 0 : 1);

    oscillator.connect(gainNode);
    gainNode.connect(offlineContext.destination);

    oscillator.start(0);
    oscillator.stop(duration);

    // Render the audio
    const renderedBuffer = await offlineContext.startRendering();
    return renderedBuffer;
  };

  // Export stems
  const exportStems = async () => {
    if (selectedTracks.size === 0) {
      alert('Please select at least one track to export');
      return;
    }

    setIsExporting(true);
    setExportProgress(0);

    try {
      const zip = new JSZip();
      const selectedTrackIds = Array.from(selectedTracks);
      const totalTracks = selectedTrackIds.length;

      // Create project info file
      const projectInfo = {
        projectName: 'Music Legend Project',
        exportDate: new Date().toISOString(),
        format: exportSettings.format,
        bitDepth: exportSettings.bitDepth,
        sampleRate: exportSettings.sampleRate,
        tracks: selectedTrackIds.map((id) => {
          const track = channels.find((ch) => ch.id === id);
          return {
            id,
            name: track?.name || 'Unknown',
            color: track?.color || '#ffffff',
          };
        }),
      };

      zip.file('project-info.json', JSON.stringify(projectInfo, null, 2));

      // Render and export each selected track
      for (let i = 0; i < selectedTrackIds.length; i++) {
        const trackId = selectedTrackIds[i];
        const track = channels.find((ch) => ch.id === trackId);
        if (!track) continue;

        // Update progress
        setExportProgress(Math.round(((i + 0.5) / totalTracks) * 100));

        // Render track
        const audioBuffer = await renderTrack(trackId);

        // Convert to WAV
        const bitDepth = parseInt(exportSettings.bitDepth);
        const wavData = audioBufferToWav(audioBuffer, bitDepth);

        // Sanitize filename
        const sanitizedName = track.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const filename = `${i + 1}_${sanitizedName}.${exportSettings.format}`;

        // Add to ZIP
        zip.file(filename, wavData);

        // Update progress
        setExportProgress(Math.round(((i + 1) / totalTracks) * 100));
      }

      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Download ZIP
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stems_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log('Stems exported successfully');
    } catch (error) {
      console.error('Failed to export stems:', error);
      alert('Failed to export stems. Please try again.');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <FileAudio className="w-5 h-5" />
            Stem Exporter
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            Export individual tracks as separate audio files
          </p>
        </div>
        <button
          onClick={exportStems}
          disabled={isExporting || selectedTracks.size === 0}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors"
        >
          <Download className="w-4 h-4" />
          {isExporting ? `Exporting... ${exportProgress}%` : 'Export Stems'}
        </button>
      </div>

      {/* Export Settings */}
      <div className="bg-zinc-800 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 text-white font-medium">
          <Settings className="w-4 h-4" />
          Export Settings
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* Format */}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Format</label>
            <select
              value={exportSettings.format}
              onChange={(e) =>
                setExportSettings({ ...exportSettings, format: e.target.value as ExportFormat })
              }
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="wav">WAV</option>
              <option value="mp3">MP3</option>
            </select>
          </div>

          {/* Bit Depth */}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Bit Depth</label>
            <select
              value={exportSettings.bitDepth}
              onChange={(e) =>
                setExportSettings({ ...exportSettings, bitDepth: e.target.value as BitDepth })
              }
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
              disabled={exportSettings.format === 'mp3'}
            >
              <option value="16">16-bit</option>
              <option value="24">24-bit</option>
              <option value="32">32-bit</option>
            </select>
          </div>

          {/* Sample Rate */}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Sample Rate</label>
            <select
              value={exportSettings.sampleRate}
              onChange={(e) =>
                setExportSettings({ ...exportSettings, sampleRate: e.target.value as SampleRate })
              }
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="44100">44.1 kHz</option>
              <option value="48000">48 kHz</option>
              <option value="96000">96 kHz</option>
            </select>
          </div>
        </div>
      </div>

      {/* Track Selection */}
      <div className="bg-zinc-800 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-white font-medium">
            Select Tracks ({selectedTracks.size} of {channels.length})
          </div>
          <div className="flex gap-2">
            <button
              onClick={selectAll}
              className="px-3 py-1 text-sm bg-zinc-700 hover:bg-zinc-600 text-white rounded transition-colors"
            >
              Select All
            </button>
            <button
              onClick={deselectAll}
              className="px-3 py-1 text-sm bg-zinc-700 hover:bg-zinc-600 text-white rounded transition-colors"
            >
              Deselect All
            </button>
          </div>
        </div>

        {/* Track List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {channels.map((track) => (
            <label
              key={track.id}
              className="flex items-center gap-3 p-3 bg-zinc-900 hover:bg-zinc-850 rounded-lg cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={selectedTracks.has(track.id)}
                onChange={() => toggleTrack(track.id)}
                className="w-4 h-4 rounded border-zinc-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
              />
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: track.color }}
              />
              <span className="text-white flex-1">{track.name}</span>
              <span className="text-sm text-zinc-400">
                {track.frozen ? 'Frozen' : 'Live'}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Export Progress */}
      {isExporting && (
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">Exporting...</span>
            <span className="text-zinc-400">{exportProgress}%</span>
          </div>
          <div className="w-full bg-zinc-700 rounded-full h-2 overflow-hidden">
            <div
              className="bg-blue-600 h-full transition-all duration-300"
              style={{ width: `${exportProgress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
